import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../utils/backendURL';
import { showSuccess, showError, showWarning, showInfo } from '../utils/notifications';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import {
  Plus,
  Key,
  Copy,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Trash2,
  RefreshCw,
  GitBranch,
  Github,
  Code,
  FileCode,
  Terminal,
  Eye,
  EyeOff,
  ExternalLink
} from 'lucide-react';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const CICD = ({ user }) => {
  // Get sub-tab from URL or default to 'api-keys'
  const getInitialSubTab = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'api-keys';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialSubTab);
  
  // Update URL when sub-tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(window.location.search);
    params.set('tab', tab);
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({ tab }, '', newUrl);
  };
  const [apiKeys, setApiKeys] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateKeyModal, setShowCreateKeyModal] = useState(false);
  const [showKeyCreatedModal, setShowKeyCreatedModal] = useState(false);
  const [newKeyData, setNewKeyData] = useState(null);
  const [keyForm, setKeyForm] = useState({
    key_name: '',
    permissions: ['trigger_tests', 'read_results']
  });

  // Delete confirmation modal state
  const [showDeleteKeyModal, setShowDeleteKeyModal] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);

  useEffect(() => {
    loadApiKeys();
    loadExecutions();
  }, []);

  const loadApiKeys = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/ci-cd/api-keys`,
        { headers: getAuthHeaders() }
      );
      setApiKeys(response.data);
    } catch (error) {
      console.error('Failed to load API keys:', error);
    }
  };

  const loadExecutions = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/ci-cd/executions?limit=50`,
        { headers: getAuthHeaders() }
      );
      setExecutions(response.data);
    } catch (error) {
      console.error('Failed to load executions:', error);
    }
  };

  const createApiKey = async () => {
    if (!keyForm.key_name.trim()) {
      showWarning('Please enter a name for the API key');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/ci-cd/api-keys`,
        keyForm,
        { headers: getAuthHeaders() }
      );

      setNewKeyData(response.data);
      setShowCreateKeyModal(false);
      setShowKeyCreatedModal(true);
      setKeyForm({ key_name: '', permissions: ['trigger_tests', 'read_results'] });
      await loadApiKeys();
    } catch (error) {
      console.error('Failed to create API key:', error);
      showError('Failed to create API key: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const openDeleteKeyModal = (key) => {
    setKeyToDelete(key);
    setShowDeleteKeyModal(true);
  };

  const deleteApiKey = async () => {
    if (!keyToDelete) return;

    setLoading(true);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/ci-cd/api-keys/${keyToDelete.id}`,
        { headers: getAuthHeaders() }
      );
      await loadApiKeys();
      showSuccess('API key revoked successfully');
      setShowDeleteKeyModal(false);
      setKeyToDelete(null);
    } catch (error) {
      console.error('Failed to delete API key:', error);
      showError('Failed to revoke API key');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, message = 'Copied to clipboard!') => {
    navigator.clipboard.writeText(text);
    const toast = document.createElement('div');
    toast.className = 'fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50';
    toast.innerHTML = `
      <div class="flex items-center gap-2">
        <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        <span>${message}</span>
      </div>
    `;
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transition = 'opacity 0.3s';
      setTimeout(() => toast.remove(), 300);
    }, 2000);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
      case 'passed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'running':
        return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'passed':
        return 'bg-green-500';
      case 'failed':
        return 'bg-red-500';
      case 'running':
        return 'bg-blue-500';
      case 'pending':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  // Use production URL
  const API_BASE_URL = "https://lexa.run";

  const githubActionsYaml = `name: Lexa.run Tests
on: [push, pull_request]

jobs:
  test:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v3
      
      - name: Run Lexa Tests
        run: |
          curl -X POST "${API_BASE_URL}/api/ci-cd/trigger-test" \\
            -H "X-API-Key: \${LEXA_API_KEY}" \\
            -H "Content-Type: application/json" \\
            -d '{
              "test_id": "YOUR_TEST_ID",
              "test_type": "flow_test",
              "repository": "\${{ github.repository }}",
              "branch": "\${{ github.ref_name }}",
              "commit_sha": "\${{ github.sha }}",
              "commit_message": "\${{ github.event.head_commit.message }}",
              "pr_number": \${{ github.event.pull_request.number || null }}
            }'
        env:
          LEXA_API_KEY: \${{ secrets.LEXA_API_KEY }}`;

  const gitlabCIYaml = `# .gitlab-ci.yml
lexa_tests:
  stage: test
  script:
    - |
      curl -X POST "${API_BASE_URL}/api/ci-cd/trigger-test" \\
        -H "X-API-Key: \${LEXA_API_KEY}" \\
        -H "Content-Type: application/json" \\
        -d '{
          "test_id": "YOUR_TEST_ID",
          "test_type": "flow_test",
          "repository": "'\${CI_PROJECT_PATH}'",
          "branch": "'\${CI_COMMIT_REF_NAME}'",
          "commit_sha": "'\${CI_COMMIT_SHA}'",
          "commit_message": "'\${CI_COMMIT_MESSAGE}'"
        }'
  only:
    - merge_requests
    - main`;

  const jenkinsGroovy = `// Jenkinsfile
pipeline {
    agent any
    
    environment {
        LEXA_API_KEY = credentials('lexa-api-key')
    }
    
    stages {
        stage('Run Lexa Tests') {
            steps {
                sh '''
                    curl -X POST "${API_BASE_URL}/api/ci-cd/trigger-test" \\
                      -H "X-API-Key: \${LEXA_API_KEY}" \\
                      -H "Content-Type: application/json" \\
                      -d '{
                        "test_id": "YOUR_TEST_ID",
                        "test_type": "flow_test",
                        "repository": "'\${GIT_URL}'",
                        "branch": "'\${GIT_BRANCH}'",
                        "commit_sha": "'\${GIT_COMMIT}'"
                      }'
                '''
            }
        }
    }
}`;

  const circleCIYaml = `# .circleci/config.yml
version: 2.1

jobs:
  test:
    docker:
      - image: cimg/base:stable
    steps:
      - checkout
      - run:
          name: Run Lexa Tests
          command: |
            curl -X POST "${API_BASE_URL}/api/ci-cd/trigger-test" \\
              -H "X-API-Key: \${LEXA_API_KEY}" \\
              -H "Content-Type: application/json" \\
              -d '{
                "test_id": "YOUR_TEST_ID",
                "test_type": "flow_test",
                "repository": "'\${CIRCLE_PROJECT_USERNAME}/\${CIRCLE_PROJECT_REPONAME}'",
                "branch": "'\${CIRCLE_BRANCH}'",
                "commit_sha": "'\${CIRCLE_SHA1}'"
              }'

workflows:
  version: 2
  test:
    jobs:
      - test`;

  const [selectedPlatform, setSelectedPlatform] = useState('github');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">CI/CD Integration</h2>
        <p className="text-gray-600">Automate your testing with any CI/CD platform - GitHub Actions, GitLab CI, Jenkins, CircleCI, and more</p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 lg:w-[600px]">
          <TabsTrigger value="api-keys">
            <Key className="h-4 w-4 mr-2" />
            API Keys
          </TabsTrigger>
          <TabsTrigger value="setup">
            <FileCode className="h-4 w-4 mr-2" />
            Setup Guide
          </TabsTrigger>
          <TabsTrigger value="history">
            <Clock className="h-4 w-4 mr-2" />
            Execution History
          </TabsTrigger>
        </TabsList>

        {/* API Keys Tab */}
        <TabsContent value="api-keys" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Your API Keys</h3>
            <Button onClick={() => setShowCreateKeyModal(true)} className="bg-purple-600 hover:bg-purple-700">
              <Plus className="h-4 w-4 mr-2" />
              Create API Key
            </Button>
          </div>

          {apiKeys.filter(key => key.is_active).length === 0 ? (
            <Card className="p-12 text-center">
              <Key className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Active API Keys</h3>
              <p className="text-gray-600 mb-6">
                Create an API key to start integrating Lexa tests with your CI/CD pipeline
              </p>
              <Button onClick={() => setShowCreateKeyModal(true)} className="bg-purple-600 hover:bg-purple-700">
                <Plus className="h-4 w-4 mr-2" />
                Create Your First API Key
              </Button>
            </Card>
          ) : (
            <div className="grid gap-4">
              {apiKeys
                .filter(key => key.is_active) // Only show active keys
                .sort((a, b) => a.key_name.localeCompare(b.key_name)) // Sort alphabetically
                .map((key) => (
                <Card key={key.id} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-gray-900">{key.key_name}</h4>
                        {key.is_active && (
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                            Active
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded font-mono">
                          {key.api_key_preview}
                        </code>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span>Created {new Date(key.created_at).toLocaleDateString()}</span>
                        {key.last_used_at && (
                          <span>• Last used {new Date(key.last_used_at).toLocaleDateString()}</span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {key.permissions.map((perm) => (
                          <Badge key={perm} variant="outline" className="text-xs">
                            {perm.replace('_', ' ')}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => openDeleteKeyModal(key)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Setup Guide Tab */}
        <TabsContent value="setup" className="space-y-6">
          {/* Platform Selector */}
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={selectedPlatform === 'github' ? 'default' : 'outline'}
              onClick={() => setSelectedPlatform('github')}
              className="flex items-center gap-2"
            >
              <Github className="h-4 w-4" />
              GitHub Actions
            </Button>
            <Button
              variant={selectedPlatform === 'gitlab' ? 'default' : 'outline'}
              onClick={() => setSelectedPlatform('gitlab')}
            >
              GitLab CI
            </Button>
            <Button
              variant={selectedPlatform === 'jenkins' ? 'default' : 'outline'}
              onClick={() => setSelectedPlatform('jenkins')}
            >
              Jenkins
            </Button>
            <Button
              variant={selectedPlatform === 'circleci' ? 'default' : 'outline'}
              onClick={() => setSelectedPlatform('circleci')}
            >
              CircleCI
            </Button>
          </div>

          {/* GitHub Actions */}
          {selectedPlatform === 'github' && (
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Github className="h-6 w-6 text-gray-900" />
                <h3 className="text-lg font-semibold">GitHub Actions Integration</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Step 1: Create an API Key</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Go to the "API Keys" tab and create a new API key for your CI/CD integration.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Step 2: Add Secret to GitHub</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    In your GitHub repository, go to Settings → Secrets and variables → Actions, then add:
                  </p>
                  <ul className="list-disc list-inside text-sm text-gray-600 ml-4">
                    <li><code className="bg-gray-100 px-1">LEXA_API_KEY</code> = Your API key</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Step 3: Create Workflow File</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Create <code className="bg-gray-100 px-1">.github/workflows/lexa-tests.yml</code> in your repository:
                  </p>
                  <div className="relative bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="absolute top-2 right-2 text-white hover:bg-gray-800"
                      onClick={() => copyToClipboard(githubActionsYaml, 'Workflow copied!')}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <pre className="text-xs font-mono">
                      {githubActionsYaml}
                    </pre>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Step 4: Find Your Test ID</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Replace <code className="bg-gray-100 px-1">YOUR_TEST_ID</code> with the actual test ID from your Flow Tests or API Tests.
                    You can find the test ID in the test details or URL.
                  </p>
                </div>

                <Alert className="bg-blue-50 border-blue-200">
                  <AlertTriangle className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    <strong>Tip:</strong> You can trigger multiple tests by making multiple API calls in your workflow.
                    Check the execution history in the "Execution History" tab to see results.
                  </AlertDescription>
                </Alert>
              </div>
            </Card>
          )}

          {/* GitLab CI */}
          {selectedPlatform === 'gitlab' && (
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Code className="h-6 w-6 text-orange-600" />
                <h3 className="text-lg font-semibold">GitLab CI Integration</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Step 1: Create an API Key</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Go to the "API Keys" tab and create a new API key.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Step 2: Add CI/CD Variable</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    In GitLab, go to Settings → CI/CD → Variables, then add:
                  </p>
                  <ul className="list-disc list-inside text-sm text-gray-600 ml-4">
                    <li><code className="bg-gray-100 px-1">LEXA_API_KEY</code> = Your API key (mark as "Masked")</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Step 3: Update .gitlab-ci.yml</h4>
                  <div className="relative bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="absolute top-2 right-2 text-white hover:bg-gray-800"
                      onClick={() => copyToClipboard(gitlabCIYaml, 'Config copied!')}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <pre className="text-xs font-mono">
                      {gitlabCIYaml}
                    </pre>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Jenkins */}
          {selectedPlatform === 'jenkins' && (
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Terminal className="h-6 w-6 text-red-600" />
                <h3 className="text-lg font-semibold">Jenkins Integration</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Step 1: Create an API Key</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Go to the "API Keys" tab and create a new API key.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Step 2: Add Credential to Jenkins</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    In Jenkins, go to Manage Jenkins → Credentials, then add:
                  </p>
                  <ul className="list-disc list-inside text-sm text-gray-600 ml-4">
                    <li>Kind: Secret text</li>
                    <li>Secret: Your API key</li>
                    <li>ID: <code className="bg-gray-100 px-1">lexa-api-key</code></li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Step 3: Create/Update Jenkinsfile</h4>
                  <div className="relative bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="absolute top-2 right-2 text-white hover:bg-gray-800"
                      onClick={() => copyToClipboard(jenkinsGroovy, 'Jenkinsfile copied!')}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <pre className="text-xs font-mono">
                      {jenkinsGroovy}
                    </pre>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* CircleCI */}
          {selectedPlatform === 'circleci' && (
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Code className="h-6 w-6 text-green-600" />
                <h3 className="text-lg font-semibold">CircleCI Integration</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Step 1: Create an API Key</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Go to the "API Keys" tab and create a new API key.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Step 2: Add Environment Variable</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    In CircleCI, go to Project Settings → Environment Variables, then add:
                  </p>
                  <ul className="list-disc list-inside text-sm text-gray-600 ml-4">
                    <li><code className="bg-gray-100 px-1">LEXA_API_KEY</code> = Your API key</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Step 3: Update .circleci/config.yml</h4>
                  <div className="relative bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="absolute top-2 right-2 text-white hover:bg-gray-800"
                      onClick={() => copyToClipboard(circleCIYaml, 'Config copied!')}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <pre className="text-xs font-mono">
                      {circleCIYaml}
                    </pre>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* API Documentation */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Terminal className="h-6 w-6 text-gray-900" />
              <h3 className="text-lg font-semibold">API Documentation</h3>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Trigger Test</h4>
                <code className="text-sm bg-gray-100 px-2 py-1 rounded block mb-2">
                  POST {API_BASE_URL}/api/ci-cd/trigger-test
                </code>
                <p className="text-sm text-gray-600">Headers: <code className="bg-gray-100 px-1">X-API-Key: your_api_key</code></p>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Get Test Status</h4>
                <code className="text-sm bg-gray-100 px-2 py-1 rounded block mb-2">
                  GET {API_BASE_URL}/api/ci-cd/test-status/{'<cicd_execution_id>'}
                </code>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Get Test Results</h4>
                <code className="text-sm bg-gray-100 px-2 py-1 rounded block mb-2">
                  GET {API_BASE_URL}/api/ci-cd/test-results/{'<cicd_execution_id>'}
                </code>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Execution History Tab */}
        <TabsContent value="history" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">CI/CD Test Executions</h3>
            <Button onClick={loadExecutions} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>

          {executions.length === 0 ? (
            <Card className="p-12 text-center">
              <GitBranch className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No CI/CD Executions Yet</h3>
              <p className="text-gray-600">
                Test executions triggered via CI/CD will appear here
              </p>
            </Card>
          ) : (
            <div className="grid gap-4">
              {executions.map((exec) => (
                <Card key={exec.id} className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        {getStatusIcon(exec.status)}
                        <h4 className="font-semibold text-gray-900 truncate">
                          {exec.test_type === 'flow_test' ? 'Flow Test' : 'API Test'}
                        </h4>
                        <Badge className={getStatusColor(exec.status) + ' text-white'}>
                          {exec.status}
                        </Badge>
                      </div>

                      {exec.repository && (
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                          <Github className="h-4 w-4" />
                          <span className="truncate">{exec.repository}</span>
                        </div>
                      )}

                      {exec.branch && (
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                          <GitBranch className="h-4 w-4" />
                          <span>{exec.branch}</span>
                          {exec.commit_sha && (
                            <code className="bg-gray-100 px-1 rounded text-xs">
                              {exec.commit_sha.substring(0, 7)}
                            </code>
                          )}
                        </div>
                      )}

                      {exec.commit_message && (
                        <p className="text-sm text-gray-600 mb-2 truncate">
                          "{exec.commit_message}"
                        </p>
                      )}

                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>{new Date(exec.started_at).toLocaleString()}</span>
                        {exec.duration_ms && (
                          <span>• {(exec.duration_ms / 1000).toFixed(2)}s</span>
                        )}
                        {exec.pr_number && (
                          <span>• PR #{exec.pr_number}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create API Key Modal */}
      <Dialog open={showCreateKeyModal} onOpenChange={setShowCreateKeyModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create CI/CD API Key</DialogTitle>
            <DialogDescription>
              Create a new API key for CI/CD integration. Keep it secure!
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Key Name
              </label>
              <Input
                placeholder="e.g., GitHub Actions - Main Repo"
                value={keyForm.key_name}
                onChange={(e) => setKeyForm({ ...keyForm, key_name: e.target.value })}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Permissions
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={keyForm.permissions.includes('trigger_tests')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setKeyForm({
                          ...keyForm,
                          permissions: [...keyForm.permissions, 'trigger_tests']
                        });
                      } else {
                        setKeyForm({
                          ...keyForm,
                          permissions: keyForm.permissions.filter(p => p !== 'trigger_tests')
                        });
                      }
                    }}
                    className="rounded"
                  />
                  <span className="text-sm">Trigger Tests</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={keyForm.permissions.includes('read_results')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setKeyForm({
                          ...keyForm,
                          permissions: [...keyForm.permissions, 'read_results']
                        });
                      } else {
                        setKeyForm({
                          ...keyForm,
                          permissions: keyForm.permissions.filter(p => p !== 'read_results')
                        });
                      }
                    }}
                    className="rounded"
                  />
                  <span className="text-sm">Read Test Results</span>
                </label>
              </div>
            </div>

            <Button
              onClick={createApiKey}
              disabled={loading}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create API Key
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* API Key Created Modal */}
      <Dialog open={showKeyCreatedModal} onOpenChange={setShowKeyCreatedModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>API Key Created Successfully!</DialogTitle>
            <DialogDescription>
              Save this API key now - you won't be able to see it again.
            </DialogDescription>
          </DialogHeader>
          {newKeyData && (
            <div className="space-y-4">
              <Alert className="bg-yellow-50 border-yellow-200">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-800">
                  <strong>Important:</strong> Copy and save this API key securely. For security reasons, we cannot show it to you again.
                </AlertDescription>
              </Alert>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Your API Key
                </label>
                <div className="flex gap-2">
                  <Input
                    value={newKeyData.api_key}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    onClick={() => copyToClipboard(newKeyData.api_key, 'API key copied!')}
                    variant="outline"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <Button
                onClick={() => {
                  setShowKeyCreatedModal(false);
                  setNewKeyData(null);
                }}
                className="w-full"
              >
                I've Saved My API Key
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete API Key Confirmation Modal */}
      <Dialog open={showDeleteKeyModal} onOpenChange={setShowDeleteKeyModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Revoke API Key
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently revoke the API key and any systems using it will lose access.
            </DialogDescription>
          </DialogHeader>
          
          {keyToDelete && (
            <div className="py-4">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  {keyToDelete.key_name}
                </p>
                <p className="text-xs text-gray-600 mb-2">
                  Key ID: {keyToDelete.id}
                </p>
                <div className="flex flex-wrap gap-1">
                  {keyToDelete.permissions.map(permission => (
                    <Badge key={permission} variant="outline" className="text-xs">
                      {permission.replace('_', ' ')}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <Alert className="bg-red-50 border-red-200 mb-4">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  <strong>Warning:</strong> Any CI/CD systems or scripts using this API key will immediately lose access.
                </AlertDescription>
              </Alert>
              
              <p className="text-sm text-gray-700 mb-4">
                Are you sure you want to revoke this API key?
              </p>
              
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteKeyModal(false);
                    setKeyToDelete(null);
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={deleteApiKey}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Revoking...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Revoke API Key
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CICD;
